/**
 * MeteoSkillEffect — Astral dense-path active: channeled AoE bombardment.
 *
 * A 2-second channel locks the player in place, then mass fragments slam
 * down onto the target area. Each meteor impact deals AoE damage and spawns
 * a gravity distortion zone that slows enemies walking through it.
 *
 * Phase sequence: windup (2000ms channel) → active (0ms, instantaneous
 * meteor dispatch) → cooldown (8000ms long tier).
 *
 * During the windup phase, player movement is locked via the movement dep.
 * The channel is NOT cancellable — it always completes once started.
 *
 * Upgrade hooks (resolver-routed):
 * - Wider Impact (meteo_upgrade_1): `impactRadiusTiles` (additive) +
 *   `distortionDurationMs` (additive) expand zone size and duration.
 * - Cascade (meteo_upgrade_2): `bonusMeteors` (additive) increases
 *   fragment count from base 3 to 5.
 *
 * Gravity Scar interaction: the passive (meteo_passive) registers a
 * DeathTriggerSystem trigger externally; this class exposes `lastImpacts`
 * so the passive can identify Meteo kills.
 *
 * Design values (from design-decisions.md § Astral Base Values):
 * - Base meteor count: 3
 * - Impact damage per meteor: 15
 * - Distortion zone radius: 1.5 tiles
 * - Distortion zone duration: 2000ms
 * - Channel duration: 2000ms
 * - Cooldown: 8000ms (long tier)
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § meteo
 * @see design-decisions.md § Astral Base Values
 * @see ADR-064 (attunement shared systems architecture)
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface MeteoDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly zones: {
    create(config: MeteoZoneConfig): string;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly movement: {
    lock(): void;
    unlock(): void;
  };
  readonly enemies: {
    getEnemiesInRadius(position: { x: number; y: number }, radiusTiles: number): string[];
  };
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
}

export interface MeteoZoneConfig {
  id: string;
  type: string;
  sourceSkillId: string;
  shape: 'circle';
  position: { x: number; y: number };
  radiusTiles: number;
  durationMs: number;
  speedMultiplier: number;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface MeteoConfig {
  /** Long-tier cooldown in ms (8000). */
  readonly cooldownMs: number;
  /** Channel duration in ms (2000). */
  readonly channelDurationMs: number;
  /** Base number of meteors fired (3). */
  readonly baseMeteorCount: number;
  /** Damage dealt per meteor impact (15). */
  readonly impactDamagePerMeteor: number;
  /** Base impact/zone radius in tiles (1.5). */
  readonly baseImpactRadiusTiles: number;
  /** Base gravity distortion zone duration in ms (2000). */
  readonly baseDistortionDurationMs: number;
  /** Speed multiplier inside distortion zones (0.5 = 50% slow). */
  readonly distortionSpeedMultiplier: number;
  /**
   * Spread radius in tiles — bonus meteors land at random offsets
   * within this distance of the player position. Base meteor lands
   * at the player position; additional meteors are spread around it.
   */
  readonly impactSpreadRadiusTiles: number;
}

const DEFAULT_CONFIG: MeteoConfig = {
  cooldownMs: 8000,
  channelDurationMs: 2000,
  baseMeteorCount: 3,
  impactDamagePerMeteor: 15,
  baseImpactRadiusTiles: 1.5,
  baseDistortionDurationMs: 2000,
  distortionSpeedMultiplier: 0.5,
  impactSpreadRadiusTiles: 2,
};

import { TILE_SIZE } from '../../../utils/Constants.js';


// ---------------------------------------------------------------------------
// Deterministic PRNG for meteor spread
// ---------------------------------------------------------------------------

/** Simple mulberry32 PRNG seeded from activation count for reproducible spread. */
function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export interface MeteorImpact {
  position: { x: number; y: number };
  radiusTiles: number;
  damage: number;
  zoneId: string;
}

export class MeteoSkillEffect extends PhasedSkillEffect {
  override readonly skillId = 'meteo';

  private readonly deps: MeteoDeps;
  readonly config: MeteoConfig;

  private _activationsCount = 0;
  private _movementLocked = false;
  private _lastImpacts: MeteorImpact[] = [];
  private _totalDamageDealt = 0;
  private _totalZonesCreated = 0;

  constructor(deps: MeteoDeps, config: Partial<MeteoConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get activationsCount(): number { return this._activationsCount; }
  get movementLocked(): boolean { return this._movementLocked; }
  get lastImpacts(): readonly MeteorImpact[] { return this._lastImpacts; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get totalZonesCreated(): number { return this._totalZonesCreated; }

  // --- PhasedSkillEffect implementation ---

  protected override readonly phaseSequence: readonly SkillPhase[] = ['windup', 'active', 'cooldown'];

  protected override getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.channelDurationMs;
      case 'active': return 0; // instantaneous — meteors fire in onPhaseEnter
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'windup') {
      this._lockMovement();
      this._activationsCount++;
    } else if (phase === 'active') {
      this._unlockMovement();
      this._fireMeteors();
    }
  }

  // --- Internal helpers ---

  private _lockMovement(): void {
    if (!this._movementLocked) {
      this.deps.movement.lock();
      this._movementLocked = true;
    }
  }

  private _unlockMovement(): void {
    if (this._movementLocked) {
      this.deps.movement.unlock();
      this._movementLocked = false;
    }
  }

  private _fireMeteors(): void {
    const bonusMeteors = this.deps.resolver.getAdditiveEffect('meteo', 'bonusMeteors');
    const meteorCount = this.config.baseMeteorCount + bonusMeteors;

    const radiusBonus = this.deps.resolver.getAdditiveEffect('meteo', 'impactRadiusTiles');
    const resolvedRadius = this.config.baseImpactRadiusTiles + radiusBonus;

    const durationBonus = this.deps.resolver.getAdditiveEffect('meteo', 'distortionDurationMs');
    const resolvedDuration = this.config.baseDistortionDurationMs + durationBonus;

    const center = this.deps.player.getPosition();
    const spreadPx = this.config.impactSpreadRadiusTiles * TILE_SIZE;

    // Seed the PRNG from activation count for deterministic spread.
    const rng = mulberry32(this._activationsCount * 7919);

    this._lastImpacts = [];

    for (let i = 0; i < meteorCount; i++) {
      // First meteor lands at center; subsequent meteors spread.
      let px: number;
      let py: number;
      if (i === 0) {
        px = center.x;
        py = center.y;
      } else {
        const angle = rng() * Math.PI * 2;
        const dist = rng() * spreadPx;
        px = center.x + Math.cos(angle) * dist;
        py = center.y + Math.sin(angle) * dist;
      }

      const position = { x: px, y: py };

      // Deal AoE damage to enemies in the impact radius.
      const targets = this.deps.enemies.getEnemiesInRadius(position, resolvedRadius);
      for (const enemyId of targets) {
        this.deps.combat.dealDamage(enemyId, this.config.impactDamagePerMeteor);
        this._totalDamageDealt += this.config.impactDamagePerMeteor;
      }

      // Spawn gravity distortion zone at impact point.
      const zoneId = this.deps.zones.create({
        id: `meteo_distortion_${this._activationsCount - 1}_${i}`,
        type: 'gravity_distortion',
        sourceSkillId: 'meteo',
        shape: 'circle',
        position: { ...position },
        radiusTiles: resolvedRadius,
        durationMs: resolvedDuration,
        speedMultiplier: this.config.distortionSpeedMultiplier,
      });
      this._totalZonesCreated++;

      this._lastImpacts.push({
        position: { ...position },
        radiusTiles: resolvedRadius,
        damage: this.config.impactDamagePerMeteor,
        zoneId,
      });
    }
  }

  override destroy(): void {
    this._unlockMovement();
    super.destroy();
  }
}
